/**
 * Home's tide pools — which categories get one, in what order, and how their
 * figures are written. Pure, so the rules are tested without a screen.
 *
 * A pool is a budgeted category, filled to its spent share of the mark. Pools
 * run fullest first, so whatever is closest to (or over) its mark is read
 * first; ties keep the user's own order. Categories without a budget have no
 * mark to fill to, so they get no pool — they are summed in one line under the
 * pools instead.
 */

import { type Cents, formatMoney, sumCents } from "@/domain/money";

interface PoolStanding {
  category: { id: string; name: string };
  budget: Cents | null;
  spent: Cents;
  ratio: number;
}

export function poolOrder<T extends PoolStanding>(standings: readonly T[]): { pools: T[]; unbudgeted: T[] } {
  const pools = standings
    .map((standing, index) => ({ standing, index }))
    .filter(({ standing }) => standing.budget !== null)
    .sort((a, b) => b.standing.ratio - a.standing.ratio || a.index - b.index)
    .map(({ standing }) => standing);
  return { pools, unbudgeted: standings.filter((s) => s.budget === null) };
}

/** A pool's figure, always at two decimal places. */
export function poolFigure(value: Cents, currency: string): string {
  return formatMoney(value, currency);
}

/**
 * The line under the pools for the categories with no mark.
 * "Other: $0.00, no budget", or "3 categories without a budget: $120.00".
 */
export function unbudgetedLine(unbudgeted: readonly PoolStanding[], currency: string): string | null {
  const [only] = unbudgeted;
  if (only === undefined) return null;
  const total = sumCents(unbudgeted.map((s) => s.spent));
  if (unbudgeted.length === 1) return `${only.category.name}: ${formatMoney(total, currency)}, no budget`;
  return `${String(unbudgeted.length)} categories without a budget: ${formatMoney(total, currency)}`;
}
